/* eslint-disable @typescript-eslint/ban-ts-comment */
import { convertForms } from 'api/forms'
import StripeForm from 'components/Billing/StripeForm'
import usePermissions from 'hooks/usePermissions'
import * as React from 'react'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import DraggableFileUpload from 'ui/atoms/DraggableFileUpload'
import FlatCard from 'ui/atoms/FlatCard'
import Modal from 'ui/atoms/Modal'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import Page from 'ui/organisms/Page'
import { v4 as uuid4 } from 'uuid'

import {
  invalidateLoadStripeCustomer,
  useLoadStripeCustomer,
} from '@/queries/payments/useLoadStripeCustomer'

import ConvertFormList from './ConvertFormList'

function ConvertForm(): React.ReactNode {
  const [showPaymentMethodModal, setShowPaymentMethodModal] =
    React.useState(false)
  const [fileList, setFileList] = React.useState<Array<File>>([])
  const [loading, setLoading] = React.useState(false)
  const [key, setKey] = React.useState(uuid4())
  const { data: stripeCustomer } = useLoadStripeCustomer()

  const { canCreatePacketForm } = usePermissions()

  const uploadFiles = React.useCallback(async () => {
    const promises: Array<Promise<object>> = []
    fileList.forEach((file) => {
      const formData = new FormData()
      formData.append('file', file)
      promises.push(convertForms(formData))
    })
    setLoading(true)
    await Promise.all(promises)
    setLoading(false)
    toast.success('Files uploaded successfully')
    setFileList([])
    setKey(uuid4())
  }, [fileList])

  if (!canCreatePacketForm) return null

  return (
    <Page title="Upload Forms">
      <FlatCard border>
        <Space size="large">
          <div>
            <Text $bold>Let Zentake Handle Your Form Setup!</Text>
            <Text>
              We’ll convert your existing forms, assessments, documents,
              templates etc. into your Zentake account, saving you the hassle of
              doing it yourself. You'll be all set to start sending forms in no
              time!
            </Text>
          </div>
          <div>
            <Text $bold>How It Works:</Text>
            <Text>
              Once you upload your forms, we’ll start working on them right
              away. You’ll be notified when they’re completed (usually in about
              2-3 days). After that, you can request revisions or make changes
              yourself using the form builder.
            </Text>
          </div>
          <div>
            <Text $bold>Form Conversion Pricing:</Text>
            <ul className="list-inside list-disc">
              <li>$5/page</li>
            </ul>
          </div>
          <div>
            <DraggableFileUpload
              multiple
              accept=".pdf, .doc, .docx"
              key={key}
              onChange={setFileList}
            />
          </div>
          <Button
            $primary
            disabled={!fileList.length}
            loading={loading}
            onClick={async () => {
              if (!stripeCustomer?.has_payment_method) {
                setShowPaymentMethodModal(true)
              } else {
                await uploadFiles()
              }
            }}
          >
            Continue
          </Button>
          <ConvertFormList key={key} />
        </Space>
      </FlatCard>
      {showPaymentMethodModal && (
        <Modal closeButton show onHide={() => setShowPaymentMethodModal(false)}>
          <div className="ml-5">
            <Text $large $semibold className="mb-1">
              Payment Information Required
            </Text>
            <Text>
              To create your forms, please enter your credit card details
              bellow.
              <br />
              Your payment information is securely processed.
            </Text>
          </div>
          <StripeForm
            // @ts-ignore
            onChange={() => {
              setTimeout(() => {
                invalidateLoadStripeCustomer()
                  .then(() => {
                    toast.success('Payment method added successfully')
                  })
                  .catch(() => {
                    toast.error('Failed to add payment method')
                  })
                  .finally(() => {
                    setShowPaymentMethodModal(false)
                  })
              }, 2000)
            }}
            stripeCustomer={stripeCustomer}
            title=" "
          />
        </Modal>
      )}
    </Page>
  )
}

export default ConvertForm
